import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  Wallet,
  Landmark,
  BarChart3,
  Calendar,
  RefreshCw,
} from "lucide-react";
import { axiosInstance } from "../../lib/axios";

const C = {
  primary: "#0058c3",
  secondary: "#006a61",
  secondaryFixed: "#89f5e7",
  tertiary: "#555d73",
  error: "#ba1a1a",
  primaryFixed: "#d8e2ff",
};

interface DistItem { key: string; label: string; value: number; pct: number; color: string; }
interface AcctProfit { name: string; total: number; netProfit: number; resourceCost: number; commission: number; upworkFee: number; tax: number; }
export interface ExecData {
  range: { key: string; label: string };
  financial: {
    totalEarnings: number; totalNetProfit: number; totalResourceCost: number;
    totalTaxFees: number; totalCommission: number; avgMargin: number;
    distribution: { total: number; items: DistItem[] };
    accountProfitability: AcctProfit[];
  };
  categoryPerformance: {
    topPerAccount: { account: string; category: string; winRate: number }[];
    matrix: { account: string; category: string; proposals: number; bids: number; won: number; winRate: number; revenue: number; avgValue: number }[];
  };
  projectsBidding: {
    pipeline: { account: string; inQueue: number; ongoing: number; completed: number; onHold: number; cancelled: number }[];
    biddingPerformance: { account: string; open: number; won: number; lost: number }[];
    connectsBidValue: { account: string; connectsUsed: number; bidAmount: number }[];
    recentActivity: { type: string; account: string; bd: string; status: string; amount: number; escrow: number; released: number; date: string }[];
  };
  forecast: {
    cards: { forecastedRevenue: number; forecastedProfit: number; expectedClosures: number; highPotentialDeals: number };
    revenueForecastTrend: { month: string; revenue: number; netProfit: number; predicted: boolean }[];
    funnel: { stage: string; count: number }[];
  };
}

const RANGE_OPTIONS = [
  { key: "today", label: "Today" }, { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "Last 7 Days" }, { key: "last30", label: "Last 30 Days" },
  { key: "thisMonth", label: "This Month" }, { key: "lastMonth", label: "Last Month" },
  { key: "thisQuarter", label: "This Quarter" }, { key: "thisYear", label: "This Year" },
  { key: "all", label: "All Time" }, { key: "custom", label: "Custom Range" },
];

const fmtMoney = (n: number) => {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};
const fmtK = (n: number) => (Math.abs(n) >= 1000 ? `$${Math.round(n / 1000)}k` : `$${Math.round(n)}`);
const fmtDate = (iso: string) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—");
const initials = (name: string) => name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

const Panel = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-white rounded-xl border border-[#c1c6d7] shadow-sm ${className}`}>{children}</div>
);
const tooltipStyle = { contentStyle: { borderRadius: 10, border: "1px solid #c1c6d7", fontSize: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" } };
const axis = { tick: { fontSize: 11, fill: "#727786" }, axisLine: { stroke: "#e2e8f0" }, tickLine: false };

export default function ExecutiveOverviewPanel() {
  const [range, setRange] = useState("last30");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState<ExecData | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchExec = async () => {
    setLoading(true);
    try {
      const params: any = { range };
      if (range === "custom") { if (customFrom) params.from = customFrom; if (customTo) params.to = customTo; }
      const res = await axiosInstance.get("/admin/executive-overview", { params });
      setData(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { if (range !== "custom") fetchExec(); /* eslint-disable-next-line */ }, [range]);

  return (
    <div className="flex flex-col gap-6">
      {/* Filters */}
      <Panel className="p-4 flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold text-[#727786]">Date Range</span>
          <div className="relative">
            <Calendar size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#727786] pointer-events-none" />
            <select value={range} onChange={(e) => setRange(e.target.value)}
              className="bg-white border border-[#c1c6d7] rounded-md text-[13px] py-1.5 pl-8 pr-8 focus:ring-2 focus:ring-blue-400 min-w-[160px] appearance-none cursor-pointer">
              {RANGE_OPTIONS.map((o) => (<option key={o.key} value={o.key}>{o.label}</option>))}
            </select>
          </div>
        </div>
        {range === "custom" && (
          <>
            <div className="flex flex-col gap-1"><span className="text-[11px] font-semibold text-[#727786]">From</span><input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="border border-[#c1c6d7] rounded-md text-[13px] py-1.5 px-2.5" /></div>
            <div className="flex flex-col gap-1"><span className="text-[11px] font-semibold text-[#727786]">To</span><input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="border border-[#c1c6d7] rounded-md text-[13px] py-1.5 px-2.5" /></div>
            <button onClick={fetchExec} className="px-4 py-2 bg-[#0058c3] text-white text-[13px] font-semibold rounded-md hover:opacity-90">Apply</button>
          </>
        )}
        <button onClick={fetchExec} className="ml-auto flex items-center gap-2 px-4 py-2 border border-[#727786] text-[#414754] font-semibold rounded-md hover:bg-[#eff4ff] text-[13px]">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /><span className="hidden sm:inline">Refresh</span>
        </button>
      </Panel>

      {!data ? (
        <Panel className="p-12 text-center text-slate-400 text-[13px]">{loading ? "Loading executive overview…" : "No data available."}</Panel>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <GroupCard icon={Wallet} iconBg="bg-[#0070f3]/15 text-[#0058c3]" title="Earnings & Profits"
              metrics={[{ label: "Total Earnings", value: fmtMoney(data.financial.totalEarnings) }, { label: "Total Net Profit", value: fmtMoney(data.financial.totalNetProfit) }]} />
            <GroupCard icon={Landmark} iconBg="bg-[#ffdad6]/40 text-[#ba1a1a]" title="Costs & Taxes"
              metrics={[{ label: "Resource Cost", value: fmtMoney(data.financial.totalResourceCost) }, { label: "Tax & Platform", value: fmtMoney(data.financial.totalTaxFees) }]} />
            <GroupCard icon={BarChart3} iconBg="bg-[#86f2e4]/40 text-[#006a61]" title="Net Performance"
              metrics={[{ label: "Commission Paid", value: fmtMoney(data.financial.totalCommission) }, { label: "Avg Profit Margin", value: `${data.financial.avgMargin}%`, accent: true }]} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Panel className="p-6 flex flex-col">
              <h4 className="text-[14px] font-semibold text-[#0b1c30] mb-5">Financial Distribution Breakdown</h4>
              <div className="flex-1 flex flex-col md:flex-row items-center justify-center gap-6">
                <div className="relative w-48 h-48 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.financial.distribution.items.filter((d) => d.value > 0)} dataKey="value" nameKey="label" innerRadius={62} outerRadius={88} paddingAngle={2} stroke="none">
                        {data.financial.distribution.items.filter((d) => d.value > 0).map((d) => (<Cell key={d.key} fill={d.color} />))}
                      </Pie>
                      <Tooltip {...tooltipStyle} formatter={(v: any) => fmtMoney(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"><span className="text-[24px] font-bold text-[#0b1c30]">100%</span><span className="text-[10px] text-[#727786] uppercase font-bold">Total</span></div>
                </div>
                <div className="flex flex-col gap-2 w-full">
                  {data.financial.distribution.items.map((d) => (
                    <div key={d.key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: d.color }} /><span className="text-[13px] text-[#414754]">{d.label}</span></div>
                      <span className="text-[12px] font-bold text-[#0b1c30]">{d.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
            <Panel className="p-6 flex flex-col">
              <h4 className="text-[14px] font-semibold text-[#0b1c30] mb-5">Account Profitability Breakdown by Account</h4>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.financial.accountProfitability}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eff4ff" /><XAxis dataKey="name" {...axis} /><YAxis {...axis} tickFormatter={fmtK} width={45} />
                    <Tooltip {...tooltipStyle} formatter={(v: any) => fmtMoney(v)} /><Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="netProfit" name="Net Profit" stackId="a" fill={C.secondary} /><Bar dataKey="resourceCost" name="Resource Cost" stackId="a" fill={C.primary} />
                    <Bar dataKey="commission" name="Commission" stackId="a" fill={C.tertiary} /><Bar dataKey="upworkFee" name="Upwork Fee" stackId="a" fill={C.secondaryFixed} />
                    <Bar dataKey="tax" name="Tax" stackId="a" fill={C.error} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>

          <div className="flex items-center gap-2.5 mt-2"><span className="w-1.5 h-6 bg-[#0058c3] rounded-full" /><h3 className="text-[18px] font-semibold text-[#0b1c30]">Category Performance Analysis</h3></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {data.categoryPerformance.topPerAccount.map((c, i) => (
              <Panel key={c.account} className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full grid place-items-center font-bold text-[13px] shrink-0" style={{ background: [`${C.primary}1a`, `${C.secondary}1a`, `${C.tertiary}1a`, `${C.primaryFixed}80`][i % 4], color: [C.primary, C.secondary, C.tertiary, "#001a43"][i % 4] }}>{initials(c.account)}</div>
                <div className="flex flex-col min-w-0"><span className="text-[11px] text-[#727786]">{c.account}</span><span className="text-[13px] font-bold text-[#0b1c30] truncate">{c.category}</span><span className="text-[11px] text-[#006a61] font-semibold">{c.winRate}% Win Rate</span></div>
              </Panel>
            ))}
          </div>
          <Panel className="overflow-hidden">
            <div className="px-5 py-4 border-b border-[#c1c6d7] bg-[#eff4ff]/50"><h4 className="text-[14px] font-semibold text-[#0b1c30]">Category Performance Matrix</h4></div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead className="bg-[#eff4ff] text-[#414754] text-[11px]"><tr><th className="px-4 py-3">Account</th><th className="px-4 py-3">Category</th><th className="px-4 py-3 text-center">Proposals</th><th className="px-4 py-3 text-center">Bids</th><th className="px-4 py-3 text-center">Won</th><th className="px-4 py-3 text-center">Win Rate</th><th className="px-4 py-3 text-right">Revenue</th><th className="px-4 py-3 text-right">Avg Value</th></tr></thead>
                <tbody className="divide-y divide-[#c1c6d7]/40">
                  {data.categoryPerformance.matrix.map((r) => (
                    <tr key={r.account} className="hover:bg-[#eff4ff]/50 text-[13px]"><td className="px-4 py-3 font-semibold text-[#0b1c30]">{r.account}</td><td className="px-4 py-3 text-[#414754]">{r.category}</td><td className="px-4 py-3 text-center text-[#414754]">{r.proposals}</td><td className="px-4 py-3 text-center text-[#414754]">{r.bids}</td><td className="px-4 py-3 text-center text-[#414754]">{r.won}</td><td className="px-4 py-3 text-center font-bold text-[#006a61]">{r.winRate}%</td><td className="px-4 py-3 text-right font-mono text-[#0b1c30]">{fmtMoney(r.revenue)}</td><td className="px-4 py-3 text-right font-mono text-[#414754]">{fmtMoney(r.avgValue)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <div className="flex items-center gap-2.5 mt-2"><span className="w-1.5 h-6 bg-[#006a61] rounded-full" /><h3 className="text-[18px] font-semibold text-[#0b1c30]">Projects &amp; Bidding Overview</h3></div>
          <Panel className="p-6 overflow-x-auto">
            <h4 className="text-[14px] font-semibold text-[#0b1c30] mb-5">Project Pipeline Funnel</h4>
            <PipelineFunnel pipeline={data.projectsBidding.pipeline} />
          </Panel>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Panel className="p-6">
              <h4 className="text-[14px] font-semibold text-[#0b1c30] mb-5">Bidding Performance Comparison</h4>
              <div style={{ width: "100%", height: 256 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.projectsBidding.biddingPerformance}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eff4ff" /><XAxis dataKey="account" {...axis} /><YAxis {...axis} width={32} allowDecimals={false} /><Tooltip {...tooltipStyle} /><Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="won" name="Won" stackId="b" fill={C.secondary} /><Bar dataKey="lost" name="Lost" stackId="b" fill={C.error} /><Bar dataKey="open" name="Open" stackId="b" fill={C.primary} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
            <Panel className="p-6">
              <h4 className="text-[14px] font-semibold text-[#0b1c30] mb-5">Connects &amp; Bid Value ($)</h4>
              <div style={{ width: "100%", height: 256 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.projectsBidding.connectsBidValue}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eff4ff" /><XAxis dataKey="account" {...axis} /><YAxis yAxisId="l" {...axis} width={36} /><YAxis yAxisId="r" orientation="right" {...axis} width={42} tickFormatter={fmtK} /><Tooltip {...tooltipStyle} /><Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar yAxisId="l" dataKey="connectsUsed" name="Connects Used" fill={C.primaryFixed} radius={[3, 3, 0, 0]} /><Bar yAxisId="r" dataKey="bidAmount" name="Bid Amount" fill={C.primary} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>
          <Panel className="overflow-hidden">
            <div className="px-5 py-4 border-b border-[#c1c6d7] bg-[#eff4ff]/50"><h4 className="text-[14px] font-semibold text-[#0b1c30]">Recent Activity</h4></div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-left">
                <thead className="bg-[#eff4ff] text-[#414754] text-[11px]"><tr><th className="px-4 py-3">Type</th><th className="px-4 py-3">Account</th><th className="px-4 py-3">BD</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 text-right">Escrow</th><th className="px-4 py-3 text-right">Released</th><th className="px-4 py-3">Date</th></tr></thead>
                <tbody className="divide-y divide-[#c1c6d7]/40">
                  {data.projectsBidding.recentActivity.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400 text-[13px]">No activity in this period.</td></tr>
                  ) : data.projectsBidding.recentActivity.map((a, i) => (
                    <tr key={i} className="hover:bg-[#eff4ff]/50 text-[13px]">
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${a.type === "Project" ? "bg-[#0058c3]/10 text-[#0058c3]" : "bg-[#006a61]/10 text-[#006a61]"}`}>{a.type.toUpperCase()}</span></td>
                      <td className="px-4 py-3 text-[#414754]">{a.account}</td><td className="px-4 py-3 text-[#414754]">{a.bd}</td><td className="px-4 py-3 text-[#414754]">{a.status}</td>
                      <td className="px-4 py-3 text-right font-mono text-[#0b1c30]">{a.amount ? fmtMoney(a.amount) : "—"}</td><td className="px-4 py-3 text-right font-mono text-[#727786]">{a.escrow ? fmtMoney(a.escrow) : "—"}</td><td className="px-4 py-3 text-right font-mono text-[#006a61]">{a.released ? fmtMoney(a.released) : "—"}</td><td className="px-4 py-3 text-[#727786]">{fmtDate(a.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <section className="bg-gradient-to-br from-[#0058c3] to-[#003e8f] p-6 sm:p-8 rounded-2xl text-white mt-2">
            <div className="flex justify-between items-start mb-6 flex-wrap gap-2">
              <div><h3 className="text-[22px] font-bold">Forecasting &amp; Predictions</h3><p className="text-[13px] opacity-80 mt-0.5">Trend-based projections from the last 6 months of pipeline velocity</p></div>
              <span className="px-3 py-1 bg-white/20 rounded-full text-[11px] backdrop-blur-md">{data.range.label}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <ForecastCard label="Forecasted Revenue" value={fmtMoney(data.forecast.cards.forecastedRevenue)} />
              <ForecastCard label="Expected Closures" value={String(data.forecast.cards.expectedClosures)} />
              <ForecastCard label="Forecasted Profit" value={fmtMoney(data.forecast.cards.forecastedProfit)} />
              <ForecastCard label="High Potential Deals" value={String(data.forecast.cards.highPotentialDeals)} />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <div className="xl:col-span-2 bg-white/5 rounded-xl p-4 border border-white/10">
                <h4 className="text-[12px] font-bold mb-3 uppercase opacity-80">Revenue Forecast Trend</h4>
                <div style={{ width: "100%", height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.forecast.revenueForecastTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.12)" /><XAxis dataKey="month" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.7)" }} axisLine={{ stroke: "rgba(255,255,255,0.2)" }} tickLine={false} /><YAxis tick={{ fontSize: 11, fill: "rgba(255,255,255,0.7)" }} axisLine={{ stroke: "rgba(255,255,255,0.2)" }} tickLine={false} tickFormatter={fmtK} width={45} /><Tooltip {...tooltipStyle} formatter={(v: any) => fmtMoney(v)} /><Legend wrapperStyle={{ fontSize: 11, color: "white" }} />
                      <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#ffffff" strokeWidth={2.5} dot={{ r: 3 }} /><Line type="monotone" dataKey="netProfit" name="Net Profit" stroke="#89f5e7" strokeWidth={2.5} dot={{ r: 3 }} strokeDasharray="5 4" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-4 border border-white/10 flex flex-col">
                <h4 className="text-[12px] font-bold mb-3 uppercase opacity-80">Project Pipeline Funnel</h4>
                <div className="flex flex-col items-center gap-1.5 w-full mt-2">
                  {(() => {
                    const max = Math.max(1, ...data.forecast.funnel.map((f) => f.count));
                    return data.forecast.funnel.map((f, i) => {
                      const w = Math.max(30, (f.count / max) * 100);
                      const isLast = i === data.forecast.funnel.length - 1;
                      return (<div key={f.stage} className={`py-2 rounded-md text-center text-[12px] font-semibold ${isLast ? "bg-white text-[#0058c3]" : "bg-white/20"}`} style={{ width: `${w}%` }}>{f.count} {f.stage}</div>);
                    });
                  })()}
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

const GroupCard = ({ icon: Icon, iconBg, title, metrics }: { icon: any; iconBg: string; title: string; metrics: { label: string; value: string; accent?: boolean }[] }) => (
  <Panel className="p-6 flex flex-col gap-4">
    <div className="flex items-center gap-2.5"><div className={`w-8 h-8 rounded-lg grid place-items-center ${iconBg}`}><Icon size={18} /></div><h4 className="text-[14px] font-semibold text-[#0b1c30]">{title}</h4></div>
    <div className="grid grid-cols-2 gap-4 mt-1">
      {metrics.map((m, i) => (
        <div key={m.label} className={`flex flex-col ${i === 1 ? "border-l border-[#c1c6d7]/40 pl-4" : ""}`}>
          <span className="text-[11px] text-[#727786] uppercase tracking-wider">{m.label}</span>
          <span className={`text-[18px] font-bold ${m.accent ? "text-[#006a61]" : "text-[#0b1c30]"}`}>{m.value}</span>
        </div>
      ))}
    </div>
  </Panel>
);

const ForecastCard = ({ label, value }: { label: string; value: string }) => (
  <div className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20"><span className="text-[11px] opacity-70 uppercase">{label}</span><div className="text-[22px] font-bold mt-1">{value}</div></div>
);

const PipelineFunnel = ({ pipeline }: { pipeline: ExecData["projectsBidding"]["pipeline"] }) => {
  const tot = (k: keyof (typeof pipeline)[number]) => pipeline.reduce((s, r) => s + (r[k] as number), 0);
  const CLIP_FIRST = "polygon(0% 0%, 90% 0%, 100% 50%, 90% 100%, 0% 100%)";
  const CLIP_MID = "polygon(90% 0%, 100% 50%, 90% 100%, 0% 100%, 10% 50%, 0% 0%)";
  const CLIP_LAST = "polygon(100% 0%, 100% 100%, 0% 100%, 10% 50%, 0% 0%)";
  const stages = [
    { key: "inQueue", label: "In Queue", total: tot("inQueue"), cls: "bg-[#0058c3]/10 text-[#0058c3]", clip: CLIP_FIRST, rounded: "rounded-l-lg" },
    { key: "ongoing", label: "Ongoing", total: tot("ongoing"), cls: "bg-[#0058c3] text-white", clip: CLIP_MID, rounded: "" },
    { key: "completed", label: "Completed", total: tot("completed"), cls: "bg-[#006a61] text-white", clip: CLIP_MID, rounded: "" },
    { key: "onHold", label: "On Hold", total: tot("onHold"), cls: "bg-[#555d73]/10 text-[#555d73]", clip: CLIP_MID, rounded: "" },
    { key: "cancelled", label: "Cancelled", total: tot("cancelled"), cls: "bg-[#ba1a1a]/10 text-[#ba1a1a]", clip: CLIP_LAST, rounded: "rounded-r-lg" },
  ] as const;
  return (
    <div className="flex flex-col gap-5 min-w-[800px]">
      <div className="flex items-stretch gap-1 h-24">
        {stages.map((s) => (
          <div key={s.key} className={`flex-1 relative flex flex-col items-center justify-center ${s.cls} ${s.rounded}`} style={{ clipPath: s.clip }}>
            <span className="text-[30px] font-bold leading-none">{s.total}</span>
            <span className="text-[11px] font-bold uppercase mt-1">{s.label}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-5 gap-x-4">
        {stages.map((s) => (
          <div key={s.key} className="flex flex-col gap-1 text-[11px] text-[#414754] px-1">
            {pipeline.map((r, i) => (
              <div key={r.account} className={`flex justify-between ${i < pipeline.length - 1 ? "border-b border-[#c1c6d7]/30 pb-0.5" : ""}`}>
                <span className="truncate">{r.account.split(" ")[0]}</span>
                <span className="font-bold">{r[s.key as keyof typeof r] as number}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
